import { useState, useEffect } from 'react';

/**
 * Tailwind breakpoint values (in pixels)
 * @see https://tailwindcss.com/docs/responsive-design
 */
export const BREAKPOINTS = {
  xs: 0, // < 640px (mobile)
  sm: 640, // >= 640px (small tablets, landscape phones)
  md: 768, // >= 768px (tablets)
  lg: 1024, // >= 1024px (laptops)
  xl: 1280, // >= 1280px (desktops)
  '2xl': 1536, // >= 1536px (large desktops)
} as const;

/**
 * Default mobile breakpoint - matches Tailwind's 'sm' breakpoint
 * Screens < 640px are considered mobile
 */
export const MOBILE_BREAKPOINT = BREAKPOINTS.sm;

/**
 * Hook to detect mobile viewport
 *
 * @param breakpoint - Width threshold in pixels (default: 640px = Tailwind 'sm')
 * @returns true if viewport width is less than breakpoint
 *
 * @example
 * // Default: mobile if < 640px
 * const isMobile = useIsMobile();
 *
 * @example
 * // Custom: mobile if < 768px (Tailwind 'md')
 * const isMobile = useIsMobile(768);
 *
 * @example
 * // Using named breakpoint
 * import { BREAKPOINTS } from '@/hooks/useIsMobile';
 * const isSmallScreen = useIsMobile(BREAKPOINTS.md);
 */
export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    // Use matchMedia for better performance
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Convenience hook for tablet detection (< 768px)
 * Matches Tailwind's 'md' breakpoint
 */
export function useIsTablet(): boolean {
  return useIsMobile(BREAKPOINTS.md);
}

/**
 * Convenience hook for small screen detection (< 1024px)
 * Matches Tailwind's 'lg' breakpoint
 */
export function useIsSmallScreen(): boolean {
  return useIsMobile(BREAKPOINTS.lg);
}
